import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

const REVEAL_CLASS = 'doc-scrollbar-visible'
const NATIVE_HIDDEN_CLASS = 'native-scrollbar-hidden'
const IDLE_MS_DEFAULT = 900
const IDLE_MS_REDUCED = 550
const MIN_THUMB = 32
const TRACK_PAD_Y = 10

function useFinePointer(): boolean {
  const [ok, setOk] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  )

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)')
    const onChange = () => setOk(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return ok
}

export const OverlayScrollbar: React.FC = () => {
  const finePointer = useFinePointer()
  const trackRef = useRef<HTMLDivElement>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const layoutRef = useRef({
    thumbH: MIN_THUMB,
    maxThumbTop: 0,
    scrollable: 0,
  })

  const [metrics, setMetrics] = useState({
    scrollHeight: 0,
    clientHeight: 0,
    scrollTop: 0,
  })
  const [trackHeight, setTrackHeight] = useState(0)
  const [thumbVisible, setThumbVisible] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReducedMotion(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (finePointer) return
    const root = document.documentElement
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    const idleMs = () => (mqReduce.matches ? IDLE_MS_REDUCED : IDLE_MS_DEFAULT)
    let t: ReturnType<typeof setTimeout> | null = null
    const clearT = () => {
      if (t) {
        clearTimeout(t)
        t = null
      }
    }
    const scheduleHide = () => {
      clearT()
      t = setTimeout(() => {
        root.classList.remove(REVEAL_CLASS)
        t = null
      }, idleMs())
    }
    const reveal = () => {
      root.classList.add(REVEAL_CLASS)
      scheduleHide()
    }
    const passive: AddEventListenerOptions = { passive: true }
    const scrollOpts: AddEventListenerOptions = { ...passive, capture: true }
    window.addEventListener('scroll', reveal, scrollOpts)
    window.addEventListener('wheel', reveal, passive)
    window.addEventListener('touchstart', reveal, passive)
    window.addEventListener('touchmove', reveal, passive)
    return () => {
      window.removeEventListener('scroll', reveal, scrollOpts)
      window.removeEventListener('wheel', reveal, passive)
      window.removeEventListener('touchstart', reveal, passive)
      window.removeEventListener('touchmove', reveal, passive)
      clearT()
      root.classList.remove(REVEAL_CLASS)
    }
  }, [finePointer])

  useEffect(() => {
    const root = document.documentElement
    if (finePointer) {
      root.classList.add(NATIVE_HIDDEN_CLASS)
      return () => root.classList.remove(NATIVE_HIDDEN_CLASS)
    }
    root.classList.remove(NATIVE_HIDDEN_CLASS)
  }, [finePointer])

  const refreshMetrics = useCallback(() => {
    const root = document.documentElement
    setMetrics({
      scrollHeight: root.scrollHeight,
      clientHeight: root.clientHeight,
      scrollTop: root.scrollTop,
    })
    const tr = trackRef.current
    if (tr) {
      setTrackHeight(tr.clientHeight)
    }
  }, [])

  useEffect(() => {
    if (!finePointer) return
    refreshMetrics()
    const passive: AddEventListenerOptions = { passive: true }
    const scrollOpts: AddEventListenerOptions = { ...passive, capture: true }
    window.addEventListener('scroll', refreshMetrics, scrollOpts)
    window.addEventListener('resize', refreshMetrics, passive)
    const roBody = new ResizeObserver(refreshMetrics)
    roBody.observe(document.body)
    return () => {
      window.removeEventListener('scroll', refreshMetrics, scrollOpts)
      window.removeEventListener('resize', refreshMetrics, passive)
      roBody.disconnect()
    }
  }, [finePointer, refreshMetrics])

  const clearHideTimer = () => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }

  const scheduleHideOverlay = useCallback(() => {
    if (!finePointer) return
    const idleMs = reducedMotion ? IDLE_MS_REDUCED : IDLE_MS_DEFAULT
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      setThumbVisible(false)
      hideTimerRef.current = null
    }, idleMs)
  }, [finePointer, reducedMotion])

  const revealOverlay = useCallback(() => {
    if (!finePointer) return
    setThumbVisible(true)
    scheduleHideOverlay()
  }, [finePointer, scheduleHideOverlay])

  useEffect(() => {
    if (!finePointer) return
    const passive: AddEventListenerOptions = { passive: true }
    const scrollOpts: AddEventListenerOptions = { ...passive, capture: true }
    const onActivity = () => revealOverlay()
    window.addEventListener('scroll', onActivity, scrollOpts)
    window.addEventListener('wheel', onActivity, passive)
    return () => {
      window.removeEventListener('scroll', onActivity, scrollOpts)
      window.removeEventListener('wheel', onActivity, passive)
      clearHideTimer()
    }
  }, [finePointer, revealOverlay])

  const needsScroll =
    finePointer && metrics.scrollHeight > metrics.clientHeight + 2
  const scrollable = Math.max(0, metrics.scrollHeight - metrics.clientHeight)
  const trackH =
    trackHeight > 0
      ? trackHeight
      : Math.max(0, window.innerHeight - TRACK_PAD_Y * 2)
  const thumbRatio =
    metrics.scrollHeight > 0 ? metrics.clientHeight / metrics.scrollHeight : 1
  const thumbH = needsScroll ? Math.max(MIN_THUMB, thumbRatio * trackH) : 0
  const maxThumbTop = Math.max(0, trackH - thumbH)
  const thumbTop =
    needsScroll && scrollable > 0 && maxThumbTop > 0
      ? (metrics.scrollTop / scrollable) * maxThumbTop
      : 0

  layoutRef.current = { thumbH, maxThumbTop, scrollable }

  useLayoutEffect(() => {
    if (!finePointer || !needsScroll || !trackRef.current) return
    const el = trackRef.current
    const ro = new ResizeObserver(() => setTrackHeight(el.clientHeight))
    ro.observe(el)
    setTrackHeight(el.clientHeight)
    return () => ro.disconnect()
  }, [finePointer, needsScroll])

  const applyScrollFromClientY = (clientY: number) => {
    const track = trackRef.current
    const root = document.documentElement
    if (!track) return
    const { thumbH: th, maxThumbTop: maxTop, scrollable: sr } = layoutRef.current
    if (sr <= 0) return
    const rect = track.getBoundingClientRect()
    const y = clientY - rect.top
    const desiredTop = Math.min(maxTop, Math.max(0, y - th / 2))
    root.scrollTop = maxTop > 0 ? (desiredTop / maxTop) * sr : 0
  }

  const onThumbPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!needsScroll) return
    e.preventDefault()
    e.stopPropagation()
    const el = e.currentTarget
    el.setPointerCapture(e.pointerId)
    clearHideTimer()
    setThumbVisible(true)

    const pointerId = e.pointerId
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return
      applyScrollFromClientY(ev.clientY)
    }
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return
      el.releasePointerCapture(pointerId)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      scheduleHideOverlay()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  const onTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!needsScroll) return
    if ((e.target as HTMLElement).closest('[data-scrollbar-thumb]')) return
    e.preventDefault()
    applyScrollFromClientY(e.clientY)
    revealOverlay()
  }

  if (!finePointer || !needsScroll) {
    return null
  }

  const fadeMs = reducedMotion ? 0 : 350

  return (
    <div
      className="pointer-events-none fixed inset-y-0 right-0 z-[9999] w-3 select-none"
      aria-hidden
    >
      <div
        ref={trackRef}
        className="pointer-events-auto absolute inset-y-3 right-0 w-full"
        style={{ touchAction: 'none' }}
        onPointerDown={onTrackPointerDown}
      >
        <div
          data-scrollbar-thumb
          className="absolute left-1/2 box-border w-2 cursor-grab rounded-full border border-solid active:cursor-grabbing"
          style={{
            height: thumbH,
            transform: `translate3d(-50%, ${thumbTop}px, 0)`,
            backgroundColor: thumbVisible ? 'rgba(255, 255, 255, 0.58)' : 'rgba(255, 255, 255, 0)',
            borderColor:
              thumbVisible ?
                'rgba(255, 255, 255, 0.38)'
              : 'rgba(255, 255, 255, 0)',
            boxShadow:
              thumbVisible ?
                '0 0 0 1px rgba(22, 24, 37, 0.05)'
              : 'none',
            transition:
              reducedMotion ?
                undefined
              : `background-color ${fadeMs}ms ease, border-color ${fadeMs}ms ease, box-shadow ${fadeMs}ms ease`,
          }}
          onPointerDown={onThumbPointerDown}
        />
      </div>
    </div>
  )
}
